import { useState, useRef, useEffect, useCallback } from "react";
import { m } from "framer-motion";
import cx from "classnames";

import useScreen from "../../hooks/useScreen";
import useOnScreen from "../../hooks/useOnScreen";
import ProgressiveImage from "../common/ProgressiveImage";
import style from "./recommendations.module.css";

const testimonials = [
  {
    avatar: "https://live.staticflickr.com/65535/54047470040_8e250998e7_t.jpg",
    avatarPlaceholder:
      "https://live.staticflickr.com/65535/54047470040_8e250998e7_m.jpg",
    name: "Darcy Gray",
    role: "Project Manager, NASA Goddard",
    quote:
      "He will wholly dedicate himself to a task and hold himself to a high standard of quality work. You will not be disappointed if you hire Jon.",
  },
  {
    avatar: "https://live.staticflickr.com/65535/54047277938_1c99d866e0_t.jpg",
    avatarPlaceholder:
      "https://live.staticflickr.com/65535/54047277938_1c99d866e0_m.jpg",
    name: "Dr. Sujay V. Kumar",
    role: "Research Scientist, NASA Goddard",
    quote:
      "Jon is diligent, hardworking, thoroughly professional, and has excellent communication skills.",
  },
  {
    avatar: "https://live.staticflickr.com/65535/54047470025_c0c1dd06dc_m.jpg",
    avatarPlaceholder:
      "https://live.staticflickr.com/65535/54047470025_c0c1dd06dc_t.jpg",
    name: "Perry Oddo",
    role: "Research Scientist, NASA Goddard",
    quote:
      "I can confidently say he would be an asset to any development team.",
  },
];

const Testimonials = () => {
  const screen = useScreen();
  const [alreadySeen, setAlreadySeen] = useState<boolean>(false);

  const textContainer = useRef<HTMLDivElement>(null);
  const isOnScreen = useOnScreen(textContainer);

  const parallax = useCallback((e: MouseEvent) => {
    const elem = document.querySelector("#background") as HTMLElement;
    let _w = window.innerWidth / 2;
    let _h = window.innerHeight / 2;
    let _mouseX = e.clientX;
    let _mouseY = e.clientY;
    let _depth1 = `${50 - (_mouseX - _w) * 0.001}% ${
      50 - (_mouseY - _h) * 0.001
    }%`;
    let _depth2 = `${50 - (_mouseX - _w) * 0.001}% ${
      50 - (_mouseY - _h) * 0.001
    }%`;
    let _depth3 = `${50 - (_mouseX - _w) * 0.001}% ${
      50 - (_mouseY - _h) * 0.001
    }%`;
    let x = `${_depth3}, ${_depth2}, ${_depth1}`;

    elem.style.backgroundPosition = x;
  }, []);

  useEffect(() => {
    if (isOnScreen && !alreadySeen) {
      const tiltBox = document.querySelector("#tiltBox") as HTMLElement;
      const background = document.querySelector("#background") as HTMLElement;
      tiltBox.classList.add(style.tiltBoxBorderHovered);

      let indexPosition = 0;
      const positions = [
        {
          transform: "rotateX(1deg) rotateY(-1.5deg)",
          backgroundPosition: "translateX(0.2%)",
        },
        {
          transform: "rotateX(2deg) rotateY(2deg)",
          backgroundPosition: "translateX(-0.2%)",
        },
        {
          transform: "",
          backgroundPosition: "",
        },
      ];

      const intervalId = setInterval(() => {
        tiltBox.style.perspective = "1000px";
        tiltBox.style.transformStyle = "preserve-3d";
        tiltBox.style.transform = positions[indexPosition]?.transform;
        background.style.transform =
          positions[indexPosition]?.backgroundPosition;
        if (indexPosition === positions.length - 1) {
          tiltBox.classList.remove(style.tiltBoxBorderHovered);
        }
        indexPosition++;
      }, 600);
      setTimeout(() => setAlreadySeen(true), 2000);
      return () => clearInterval(intervalId);
    }
  }, [isOnScreen, alreadySeen]);

  return (
    <div
      id="background"
      className={cx(
        "h-[100vh] w-full transition ease-in flex items-center justify-center pb-[200px] pt-[100px] sm:pb-52 sm:pt-52",
        style.background
      )}
    >
      <div
        className={cx("flex items-center justify-center", style.tiltBoxWrap)}
        onMouseEnter={() => document.addEventListener("mousemove", parallax)}
        onMouseLeave={() => document.removeEventListener("mousemove", parallax)}
      >
        <span className={cx(style.tOver)}></span>
        <span className={cx(style.tOver)}></span>
        <span className={cx(style.tOver)}></span>
        <span className={cx(style.tOver)}></span>
        <span className={cx(style.tOver)}></span>
        <span className={cx(style.tOver)}></span>
        <span className={cx(style.tOver)}></span>
        <span className={cx(style.tOver)}></span>
        <span className={cx(style.tOver)}></span>
        <div
          id="tiltBox"
          className={cx(
            "px-8 py-8 sm:px-20 sm:py-20 my-32 rounded-3xl text-sm sm:text-base text-green-800 flex flex-col items-center justify-center",
            style.tiltBox,
            screen.md && style.tiltBoxBorder
          )}
        >
          <div ref={textContainer} className="flex flex-col gap-10 sm:gap-12">
            {testimonials.map(
              ({ name, role, avatar, avatarPlaceholder, quote }, i) => (
                <m.div
                  key={name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.15 }}
                  className="flex flex-col sm:flex-row items-center sm:items-start gap-4"
                >
                  <div className="shrink-0 shadow-md shadow-green-900 rounded-full">
                    <ProgressiveImage
                      src={avatar}
                      placeholder={avatarPlaceholder}
                      alt={name}
                      className="h-16 w-16 rounded-full"
                    />
                  </div>
                  <div className={cx("text-center sm:text-left", style.content)}>
                    <p className="italic leading-relaxed font-body">"{quote}"</p>
                    <p className="mt-2 font-medium text-sm font-title">
                      {name}
                    </p>
                    <p className="text-xs opacity-70 font-body">{role}</p>
                  </div>
                </m.div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Testimonials;
